import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { MapPin, Loader2, RefreshCw, Search, X } from "lucide-react";
import "leaflet/dist/leaflet.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_COLORS = {
  national_scheduled: "#DC2626",
  national_unscheduled: "#F87171",
  safeguard_scheduled: "#2563EB",
  safeguard_unscheduled: "#93C5FD",
};

// Distinct accent color for the highlighted (searched) marker
const HIGHLIGHT_COLOR = "#F59E0B"; // amber-500

// Inspector home-city pins (Angela Feb 2026)
// Lime green so they stand out from national (red) and safeguard (blue) pins.
// Geocoded once at build time — city centroids are stable enough for
// route-planning context; we do not need street-level precision here.
const INSPECTOR_PIN_COLOR = "#84CC16"; // lime-500
const INSPECTOR_HOMES = [
  { name: "Fernando",  city: "Fall River, MA",     lat: 41.7015, lng: -71.1550 },
  { name: "Rodrigo",   city: "Fall River, MA",     lat: 41.7050, lng: -71.1490 },
  { name: "César",     city: "Ayer, MA",           lat: 42.5601, lng: -71.5900 },
  { name: "Lucas",     city: "Shrewsbury, MA",     lat: 42.2959, lng: -71.7134 },
  { name: "Mayara",    city: "West Boylston, MA",  lat: 42.3667, lng: -71.7856 },
  { name: "Aline",     city: "Hyde Park, MA",      lat: 42.2551, lng: -71.1256 },
  { name: "Angela",    city: "Providence, RI",     lat: 41.8240, lng: -71.4128 },
];

const LEGEND = [
  { key: "national_scheduled", label: "National - Scheduled", color: "#DC2626" },
  { key: "national_unscheduled", label: "National - Not Scheduled", color: "#F87171" },
  { key: "safeguard_scheduled", label: "Safeguard - Scheduled", color: "#2563EB" },
  { key: "safeguard_unscheduled", label: "Safeguard - Not Scheduled", color: "#93C5FD" },
];

const FitBounds = ({ orders, skip }) => {
  const map = useMap();
  useEffect(() => {
    if (skip) return;
    const points = orders.filter(o => o.lat && o.lng && o.lat !== 0).map(o => [o.lat, o.lng]);
    if (points.length > 0) {
      map.fitBounds(points, { padding: [30, 30], maxZoom: 13 });
    }
  }, [map, orders, skip]);
  return null;
};

// Programmatic focus + zoom when an order is selected via search
const FocusOnOrder = ({ order }) => {
  const map = useMap();
  useEffect(() => {
    if (order && order.lat && order.lng) {
      map.flyTo([order.lat, order.lng], 15, { duration: 0.8 });
    }
  }, [map, order]);
  return null;
};

const OperationalMap = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ orders: [], total: 0, geocoded: 0, pending_geocode: 0 });
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedOrderId, setFocusedOrderId] = useState(null);
  const [searchError, setSearchError] = useState("");
  // Two-step click: first click arms the marker (zoom+popup),
  // second click on the SAME marker opens the Order Summary.
  const [armedOrderId, setArmedOrderId] = useState(null);
  const markerRefs = useRef({});

  const fetchMapData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/map/orders`);
      setData(res.data);
    } catch (e) {
      console.error("Failed to fetch map data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMapData(); }, [fetchMapData]);

  const handleGeocoding = async () => {
    setGeocoding(true);
    try {
      const res = await axios.post(`${API}/map/geocode-batch`);
      const { geocoded, remaining } = res.data;
      if (geocoded > 0) fetchMapData();
      if (remaining > 0) {
        setTimeout(handleGeocoding, 500);
      } else {
        setGeocoding(false);
      }
    } catch {
      setGeocoding(false);
    }
  };

  const visibleOrders = useMemo(() => {
    return data.orders.filter(o => o.lat && o.lng && o.lat !== 0 && o.lng !== 0);
  }, [data.orders]);

  const categoryCounts = useMemo(() => {
    const counts = { national_scheduled: 0, national_unscheduled: 0, safeguard_scheduled: 0, safeguard_unscheduled: 0 };
    for (const o of visibleOrders) {
      if (counts[o.category] !== undefined) counts[o.category]++;
    }
    return counts;
  }, [visibleOrders]);

  const focusedOrder = useMemo(
    () => visibleOrders.find(o => o.id === focusedOrderId) || null,
    [visibleOrders, focusedOrderId]
  );

  // The marker that should be visually highlighted:
  // either the one found by search or the one armed by the first click.
  const highlightedId = focusedOrderId || armedOrderId;
  const highlightedOrder = useMemo(
    () => visibleOrders.find(o => o.id === highlightedId) || null,
    [visibleOrders, highlightedId]
  );

  const performSearch = useCallback(() => {
    const term = (searchTerm || "").trim();
    if (!term) {
      setSearchError("");
      setFocusedOrderId(null);
      return;
    }
    const termLc = term.toLowerCase();

    // Try exact match first on order_number, then contains
    let match = visibleOrders.find(o => (o.order_number || "").toLowerCase() === termLc);
    if (!match) {
      match = visibleOrders.find(o => (o.order_number || "").toLowerCase().includes(termLc));
    }

    if (!match) {
      // Check if the order exists in dataset but is not geocoded
      const anyOrder = data.orders.find(o => (o.order_number || "").toLowerCase().includes(termLc));
      if (anyOrder) {
        setSearchError(`Order ${anyOrder.order_number} found but not yet on the map (pending geocoding).`);
      } else {
        setSearchError(`No internal order matching "${term}" was found on the map.`);
      }
      setFocusedOrderId(null);
      return;
    }

    setSearchError("");
    setFocusedOrderId(match.id);
    setArmedOrderId(match.id);
  }, [searchTerm, visibleOrders, data.orders]);

  // Open popup automatically on the highlighted marker after focus/arm changes
  useEffect(() => {
    if (!highlightedId) return;
    const t = setTimeout(() => {
      const ref = markerRefs.current[highlightedId];
      if (ref && typeof ref.openPopup === "function") {
        ref.openPopup();
      }
    }, 900); // slight delay so flyTo animation settles
    return () => clearTimeout(t);
  }, [highlightedId, visibleOrders]);

  const clearSearch = () => {
    setSearchTerm("");
    setSearchError("");
    setFocusedOrderId(null);
    setArmedOrderId(null);
  };

  if (!expanded) {
    return (
      <div
        className="bg-white rounded-[16px] border border-gray-200 p-4 cursor-pointer hover:border-gray-300 transition-colors mt-5 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
        onClick={() => setExpanded(true)}
        data-testid="operational-map-collapsed"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <MapPin className="h-4.5 w-4.5 text-[#2F5D8A]" />
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Scheduling Map</h3>
              <p className="text-[11px] text-gray-400">{data.geocoded} internal orders mapped</p>
            </div>
          </div>
          <span className="text-xs text-gray-400">Click to expand</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[16px] border border-gray-200 overflow-hidden mt-5 shadow-[0_1px_4px_rgba(0,0,0,0.06)]" data-testid="operational-map-section">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <MapPin className="h-4.5 w-4.5 text-[#2F5D8A]" />
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Scheduling Map - Internal Orders</h3>
            <p className="text-[11px] text-gray-400">
              {visibleOrders.length} on map / {data.total} total internal
              {data.pending_geocode > 0 && ` / ${data.pending_geocode} pending`}
            </p>
          </div>
        </div>

        {/* Search - fixed at top */}
        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-[420px]">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") performSearch(); }}
              placeholder="Search order number..."
              className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#2F5D8A] focus:ring-1 focus:ring-[#2F5D8A]/30"
              data-testid="map-search-input"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
                data-testid="map-search-clear-btn"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={performSearch}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#2F5D8A] text-white hover:bg-[#264a70] transition-colors"
            data-testid="map-search-btn"
          >
            Find
          </button>
        </div>

        <div className="flex items-center gap-2">
          {data.pending_geocode > 0 && (
            <button
              onClick={handleGeocoding}
              disabled={geocoding}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-[#2F5D8A]/10 text-[#2F5D8A] hover:bg-[#2F5D8A]/20 disabled:opacity-50 transition-colors"
              data-testid="geocode-btn"
            >
              {geocoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {geocoding ? "Geocoding..." : `Geocode (${data.pending_geocode})`}
            </button>
          )}
          <button onClick={() => setExpanded(false)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1">
            Collapse
          </button>
        </div>
      </div>

      {/* Search feedback */}
      {(searchError || focusedOrder) && (
        <div className="px-4 py-2 border-b border-gray-50 text-[11px]">
          {searchError && (
            <span className="text-red-600 font-medium" data-testid="map-search-error">{searchError}</span>
          )}
          {focusedOrder && !searchError && (
            <span className="text-[#2F5D8A] font-medium" data-testid="map-search-result">
              Found order #{focusedOrder.order_number} — {focusedOrder.city}, {focusedOrder.state}. Click the highlighted marker again (or double-click) to open the Order Summary.
            </span>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-2 border-b border-gray-50 flex items-center gap-4 flex-wrap">
        {LEGEND.map(item => (
          <div key={item.key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border border-white shadow-sm" style={{ backgroundColor: item.color }} />
            <span className="text-[10px] text-gray-600 font-medium">{item.label}</span>
            <span className="text-[10px] text-gray-400">({categoryCounts[item.key]})</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5" data-testid="legend-inspector-home">
          <div
            className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm"
            style={{ backgroundColor: INSPECTOR_PIN_COLOR }}
          />
          <span className="text-[10px] text-gray-600 font-medium">Inspector Home</span>
          <span className="text-[10px] text-gray-400">({INSPECTOR_HOMES.length})</span>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: 420 }} data-testid="map-container">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 text-[#2F5D8A] animate-spin" />
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MapPin className="h-8 w-8 mb-2" />
            <p className="text-sm">No geocoded internal orders to display</p>
            {data.pending_geocode > 0 && (
              <button onClick={handleGeocoding} className="mt-2 px-3 py-1.5 text-xs bg-[#2F5D8A] text-white rounded-lg">
                Start Geocoding ({data.pending_geocode} orders)
              </button>
            )}
          </div>
        ) : (
          <MapContainer
            center={[42.0, -71.5]}
            zoom={8}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds orders={visibleOrders} skip={!!highlightedOrder} />
            <FocusOnOrder order={highlightedOrder} />
            {/* Inspector home pins (Angela Feb 2026) — pink, slightly
                larger than order markers, permanent name label on the
                right side. Non-interactive route context. */}
            {INSPECTOR_HOMES.map(insp => (
              <CircleMarker
                key={`home-${insp.name}`}
                center={[insp.lat, insp.lng]}
                radius={9}
                pathOptions={{
                  fillColor: INSPECTOR_PIN_COLOR,
                  color: "#fff",
                  weight: 2,
                  fillOpacity: 0.95,
                }}
                data-testid={`inspector-home-pin-${insp.name.toLowerCase()}`}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold" style={{ color: INSPECTOR_PIN_COLOR }}>
                      {insp.name}
                    </div>
                    <div className="text-gray-500">Home base: {insp.city}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
            {visibleOrders.map(order => {
              const isHighlighted = order.id === highlightedId;
              return (
                <CircleMarker
                  key={order.id}
                  center={[order.lat, order.lng]}
                  radius={isHighlighted ? 12 : 6}
                  ref={(instance) => {
                    if (instance) markerRefs.current[order.id] = instance;
                    else delete markerRefs.current[order.id];
                  }}
                  pathOptions={{
                    fillColor: isHighlighted ? HIGHLIGHT_COLOR : (CATEGORY_COLORS[order.category] || "#6B7280"),
                    color: isHighlighted ? "#B45309" : "#fff",
                    weight: isHighlighted ? 3 : 1.5,
                    fillOpacity: isHighlighted ? 0.95 : 0.85,
                  }}
                  eventHandlers={{
                    click: () => {
                      // Two-step click: first click arms this marker (zoom + popup),
                      // second click on the SAME marker opens the Order Summary.
                      if (armedOrderId === order.id) {
                        navigate(`/scheduling/order/${order.id}`);
                      } else {
                        setArmedOrderId(order.id);
                        // Clear any search focus so the new selection wins.
                        setFocusedOrderId(null);
                        setSearchError("");
                      }
                    },
                    dblclick: () => {
                      navigate(`/scheduling/order/${order.id}`);
                    },
                  }}
                >
                  <Popup>
                    <div className="text-xs min-w-[180px]" data-testid={`map-popup-${order.order_number}`}>
                      <div className="font-bold text-gray-800 mb-1">{order.order_number}</div>
                      <div className="text-gray-500 mb-1.5">{order.address}, {order.city} {order.state} {order.zip_code}</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                        <span className="text-gray-400">Company:</span>
                        <span className="font-medium">{order.company}</span>
                        <span className="text-gray-400">Status:</span>
                        <span className="font-medium">{order.status}</span>
                        <span className="text-gray-400">Appointment:</span>
                        <span className="font-medium">{order.appointment_set === "yes" ? "Scheduled" : "Not scheduled"}</span>
                        {order.appointment_sg_date && (
                          <>
                            <span className="text-gray-400">Appt Date:</span>
                            <span className="font-medium">{order.appointment_sg_date} {order.appointment_sg_time || ""}</span>
                          </>
                        )}
                        <span className="text-gray-400">Due:</span>
                        <span className="font-medium">{order.due_date || "-"}</span>
                        {order.assigned_inspector && (
                          <>
                            <span className="text-gray-400">Inspector:</span>
                            <span className="font-medium">{order.assigned_inspector}</span>
                          </>
                        )}
                        {order.cliente && (
                          <>
                            <span className="text-gray-400">Client:</span>
                            <span className="font-medium">{order.cliente}</span>
                          </>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/scheduling/order/${order.id}`); }}
                        className="mt-2 w-full px-2 py-1 text-[11px] font-medium rounded-md bg-[#2F5D8A] text-white hover:bg-[#264a70]"
                        data-testid={`map-popup-open-${order.order_number}`}
                      >
                        Open Order Summary
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default OperationalMap;
